import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActions from "@mui/material/CardActions";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PixIcon from "@mui/icons-material/Pix";
import RefreshIcon from "@mui/icons-material/Refresh";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { currencyBRL } from "../../../util/DoubleUtil";
import { dateTimeFormat } from "../../../util/DateUtil";
import PixTransacaoView from "../../PixTransacao/PixTransacaoView";
import CartaoCreditoTransacaoView, {
  FORMAS_PAGAMENTO_CARTAO,
} from "../../CartaoCreditoTransacao/CartaoCreditoTransacaoView";
import CantinaPageShared from "../CantinaPageShared";

const Resumo = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2">{value || "--"}</Typography>
  </Box>
);

const SolicitacaoCard = ({ solicitacao: s, onVerificar, verificando, onDetalhes }) => {
  const pix = s.pixTransacao;
  const cartao = s.cartaoCreditoTransacao;
  // A consulta é da transação Pix: só faz sentido para Pix ainda não quitado
  // (para cartão o servidor responde 500).
  const podeVerificar = Boolean(pix) && !pix.quitado;

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <CardContent sx={{ flexGrow: 1 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start" mb={1.5}>
          <Box flexGrow={1} minWidth={0}>
            <Typography variant="subtitle1" fontWeight={600} noWrap title={s.aluno?.nome}>
              {s.aluno?.nome ?? "--"}
            </Typography>
            <Typography variant="caption" color="text.secondary" component="div" noWrap>
              #{s.codigo} · solicitado por {s.usuario?.nome ?? "--"}
            </Typography>
          </Box>
          <Typography variant="h6" fontWeight={600} sx={{ whiteSpace: "nowrap" }}>
            {currencyBRL(s.valorCredito)}
          </Typography>
        </Stack>

        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap mb={2}>
          {pix && <Chip size="small" variant="outlined" icon={<PixIcon />} label="Pix" />}
          {cartao && <Chip size="small" variant="outlined" icon={<CreditCardOutlinedIcon />} label="Cartão de crédito" />}
          {pix &&
            (pix.quitado ? (
              <Chip size="small" color="success" label="Pago" />
            ) : (
              <Chip size="small" color="warning" variant="outlined" label="Aguardando pagamento" />
            ))}
          <Chip
            size="small"
            variant="outlined"
            color={s.enviadoProvidence ? "success" : "default"}
            label={s.enviadoProvidence ? "Enviado ao Providence" : "Não enviado ao Providence"}
          />
        </Stack>

        <Grid container spacing={1.5}>
          <Grid size={6}>
            <Resumo label="Criada em" value={dateTimeFormat(s.createdAt)} />
          </Grid>
          <Grid size={6}>
            <Resumo label="Última atualização" value={dateTimeFormat(s.lastModified)} />
          </Grid>
          {pix && (
            <>
              <Grid size={6}>
                <Resumo label="Pago em" value={dateTimeFormat(pix.dataPagamento)} />
              </Grid>
              <Grid size={6}>
                <Resumo label="Valor pago" value={pix.valorPago ? currencyBRL(pix.valorPago) : null} />
              </Grid>
            </>
          )}
          {cartao && (
            <>
              <Grid size={6}>
                <Resumo
                  label="Forma de pagamento"
                  value={
                    (FORMAS_PAGAMENTO_CARTAO[cartao.cartaoFormaPagamento] ?? cartao.cartaoFormaPagamento) +
                    (cartao.quantidadeParcelas ? ` · ${cartao.quantidadeParcelas}x` : "")
                  }
                />
              </Grid>
              <Grid size={6}>
                <Resumo label="Cód. autorização" value={cartao.codigoAutorizacao} />
              </Grid>
            </>
          )}
        </Grid>

        {(pix?.detalhesErro || cartao?.detalhesErro) && (
          <Typography variant="caption" color="error.main" component="div" mt={1.5}>
            {pix?.detalhesErro || cartao?.detalhesErro}
          </Typography>
        )}
      </CardContent>

      <Divider />
      <CardActions sx={{ px: 2, justifyContent: "space-between" }}>
        <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={() => onDetalhes(s)} disabled={!pix && !cartao}>
          Detalhes
        </Button>
        {podeVerificar && (
          <Button
            size="small"
            variant="contained"
            startIcon={verificando ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
            disabled={verificando}
            onClick={() => onVerificar(s.codigo)}
          >
            Verificar Pagamento
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

const CantinaCreditoSolicitacaoPage = ({
  cantinaCreditoSolicitacaoPage,
  handleChangePage,
  handleVerificarPagamento,
  verificandoCodigo,
  isLoading,
}) => {
  const [detalhe, setDetalhe] = useState(null);

  return (
    <>
      <CantinaPageShared
        page={cantinaCreditoSolicitacaoPage}
        isLoading={isLoading}
        loadingText="Carregando solicitações..."
        emptyText="Nenhuma solicitação de crédito."
        handleChangePage={handleChangePage}
      >
        <Grid container spacing={2}>
          {cantinaCreditoSolicitacaoPage.content.map((s) => (
            <Grid key={s.codigo} size={{ xs: 12, lg: 6 }}>
              <SolicitacaoCard
                solicitacao={s}
                onVerificar={handleVerificarPagamento}
                verificando={verificandoCodigo === s.codigo}
                onDetalhes={setDetalhe}
              />
            </Grid>
          ))}
        </Grid>
      </CantinaPageShared>

      <Dialog open={Boolean(detalhe)} onClose={() => setDetalhe(null)} fullWidth maxWidth="md">
        <DialogTitle>
          Solicitação #{detalhe?.codigo}
          <Typography variant="body2" color="text.secondary">
            {detalhe?.aluno?.nome} · {currencyBRL(detalhe?.valorCredito)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            {detalhe?.pixTransacao && (
              <Box>
                <Typography variant="subtitle2" mb={1.5}>
                  Transação Pix
                </Typography>
                <PixTransacaoView pixTransacao={detalhe.pixTransacao} />
              </Box>
            )}
            {detalhe?.cartaoCreditoTransacao && (
              <Box>
                <Typography variant="subtitle2" mb={1.5}>
                  Transação Cartão de Crédito
                </Typography>
                <CartaoCreditoTransacaoView cartaoCreditoTransacao={detalhe.cartaoCreditoTransacao} />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalhe(null)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CantinaCreditoSolicitacaoPage;
